// Derives the inward "context" from server state: can the operator inward right now, and is
// the inward running inside a fulfillment orchestration (in-flight inward) rather than a
// dedicated Inward orchestration?
//
// Two consumers depend on the same answer — the nav lock in App and the slot gating on the
// Material Inward screen — so the rule lives here once. If they derived it separately they
// could disagree ("nav says locked" vs "screen says disabled"), which is the worse failure.
//
// In-flight inward mirrors the orchestrator's own gate
// (FulfillmentProcessorHelper.CheckIfDedicatedInwardGatesExist): a fulfillment run only
// processes inward when a pick station whose *original* purpose is Inward has gates.

export const INWARD_ORCHESTRATION_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const INACTIVE_ORCHESTRATION_STATUSES = new Set(["Completed"]);

export const INWARD_OPERATION_MODE = "Inward";
export const FULFILLMENT_OPERATION_MODE = "Fulfillment";
export const CYCLE_COUNTING_OPERATION_MODE = "CycleCounting";

/**
 * Operation modes that decide what the operator is allowed to do, in precedence order: an
 * exclusive run wins if the orchestrator ever reports more than one active orchestration.
 *
 * The replenishment modes are deliberately absent — `Replenishment`/`ParallelReplenishment` are
 * not in use, and a stray one must not take the nav away from fulfillment.
 */
const GATING_OPERATION_MODES = [
  CYCLE_COUNTING_OPERATION_MODE,
  INWARD_OPERATION_MODE,
  FULFILLMENT_OPERATION_MODE,
] as const;

export type GatingOperationMode = (typeof GATING_OPERATION_MODES)[number];

export interface OrchestrationSummary {
  id?: string;
  operationMode?: string;
  status?: string;
  creationDate?: string;
}

export interface InwardContext {
  /** An orchestration is running that the operator can inward against. */
  inwardActive: boolean;
  /** That orchestration is a fulfillment run — the inward is happening in flight. */
  isInflightInward: boolean;
  orchestrationId?: string;
}

export const NO_INWARD_CONTEXT: InwardContext = {
  inwardActive: false,
  isInflightInward: false,
};

function isActiveOrchestration(orchestration: any, nowMs: number): boolean {
  if (!orchestration || INACTIVE_ORCHESTRATION_STATUSES.has(orchestration.status)) {
    return false;
  }

  const createdMs = Date.parse(orchestration.creationDate ?? "");
  if (Number.isNaN(createdMs)) {
    return false;
  }

  return nowMs - createdMs <= INWARD_ORCHESTRATION_MAX_AGE_MS;
}

export function filterActiveOrchestrations(
  orchestrations: unknown,
  nowMs: number
): OrchestrationSummary[] {
  if (!Array.isArray(orchestrations)) {
    return [];
  }

  return orchestrations.filter((orchestration) =>
    isActiveOrchestration(orchestration, nowMs)
  );
}

// PickStationPurpose serialises as a string ("Inward") through the API's Newtonsoft
// StringEnumConverter, but the operator UI's own station types have long declared it a
// number, so accept the enum ordinal too rather than silently disabling inward.
function isInwardPurpose(purpose: unknown): boolean {
  if (typeof purpose === "number") {
    return purpose === 0;
  }

  return typeof purpose === "string" && purpose.trim().toLowerCase() === "inward";
}

export function hasDedicatedInwardGates(stations: unknown): boolean {
  if (!Array.isArray(stations)) {
    return false;
  }

  return stations.some(
    (station: any) =>
      Array.isArray(station?.gates) &&
      station.gates.length > 0 &&
      isInwardPurpose(station?.originalPurpose)
  );
}

/**
 * What the warehouse is doing right now, or `undefined` when nothing gating is running. Reads
 * the same `orchestrations` response as {@link resolveInwardContext} so the nav gating and the
 * inward screen can never disagree about which run is live.
 */
export function resolveActiveOperationMode(
  orchestrations: unknown,
  nowMs: number
): GatingOperationMode | undefined {
  const active = filterActiveOrchestrations(orchestrations, nowMs);

  return GATING_OPERATION_MODES.find((mode) =>
    active.some((orchestration) => orchestration.operationMode === mode)
  );
}

export function resolveInwardContext(
  orchestrations: unknown,
  stations: unknown,
  nowMs: number
): InwardContext {
  const active = filterActiveOrchestrations(orchestrations, nowMs);

  // A dedicated Inward orchestration wins: it is inward in its own right, and the operator
  // keeps the Exit Inward control that ends it.
  const dedicatedInward = active.find(
    (orchestration) => orchestration.operationMode === INWARD_OPERATION_MODE
  );
  if (dedicatedInward) {
    return {
      inwardActive: true,
      isInflightInward: false,
      orchestrationId: dedicatedInward.id,
    };
  }

  const fulfillment = active.find(
    (orchestration) => orchestration.operationMode === FULFILLMENT_OPERATION_MODE
  );
  if (fulfillment && hasDedicatedInwardGates(stations)) {
    return {
      inwardActive: true,
      isInflightInward: true,
      orchestrationId: fulfillment.id,
    };
  }

  return NO_INWARD_CONTEXT;
}

/**
 * Step name the orchestrator writes when an exit is requested
 * (`StepNames.ExitOrchestrationStep`).
 */
export const EXIT_ORCHESTRATION_STEP_NAME = "Exit Orchestration";

/**
 * Server truth for "an exit has already been requested for this orchestration".
 *
 * The orchestrator writes the exit step once, already completed
 * (`ExitOrchestrationService.CreateExitOrchestrationStepAsync`), so the step's mere
 * existence is the flag — there is no in-between state to read. `orchestrations/{id}/steps`
 * also deserialises to the base `Step` type, which carries neither `isCompleted` nor
 * `operationMode`, so `stepName` is the only field available to match on.
 *
 * `InwardProcessor` gates on exactly the same record, so the UI and the orchestrator
 * agree on when the run is winding down.
 */
export function hasExitOrchestrationStep(steps: unknown): boolean {
  if (!Array.isArray(steps)) {
    return false;
  }

  return steps.some(
    (step: any) =>
      typeof step?.stepName === "string" &&
      step.stepName.trim().toLowerCase() ===
        EXIT_ORCHESTRATION_STEP_NAME.toLowerCase()
  );
}

